import iconProfile from "@assets/header/icon-profile-new.svg";
import slideDownIcon from "@assets/header/slideDownIcon.svg";
import slideDownIconMobile from "@assets/header/slideDownIconMobile.svg";
import slideUpIconMobile from "@assets/header/slideUpIconMobile.svg";
import { ENABLE_MYBH, MANAGE_TOPICS_LINK } from "@config/followTopic";
import useMySPHLightbox, { User } from "@src/app/hooks/useMySPHLightbox";
import cx from "classnames";
import { useEffect, useState } from "react";

export type UserInfoProps = {
  rootClassName?: string;
  userInfo: User;
  variant?: "menu" | "mybh";
};

/**
 *
 * @param rootClassName top-level css class override
 * @param userInfo user data decoded from JWT
 * @see https://docs.google.com/document/d/1obM9zIuU0Cj6UlI3mkHXGWDEIAGpCx029iiuV_kEwUU/edit# for API usage of window._mySPHObj
 */
export default function UserInfo({
  rootClassName,
  userInfo,
  variant = "menu",
}: UserInfoProps): React.ReactElement {
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  const { lightbox } = useMySPHLightbox();

  const isMyBH = variant === "mybh";

  useEffect(() => {
    const handleClick = () => {
      setIsPanelVisible(false);
    };

    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  const styles = {
    button: cx(
      "flex w-full items-center justify-center",
      isMyBH ? "h-auto" : "h-[54px] lg:h-auto",
      isPanelVisible && !isMyBH && "bg-neutral-700 lg:bg-bg-primary",
    ),

    container: cx(
      "flex items-center gap-1 justify-between",
      isMyBH
        ? "w-auto h-auto px-0 text-[14px] text-brand-primary font-regular"
        : "w-full h-[54px] px-sm text-sm text-text-inverse font-semimedium lg:w-auto lg:h-auto lg:px-0 lg:text-[12px] lg:text-brand-primary lg:font-regular",
    ),

    profileIcon: cx(isMyBH ? "block" : "hidden lg:block"),

    profileName: cx(
      "w-full truncate text-left leading-[24px]",
      isMyBH
        ? "max-w-[108px] flex-grow-0"
        : "max-w-[200px] flex-grow lg:flex-grow-0",
    ),

    desktopArrow: cx(
      isMyBH ? "block" : "hidden lg:block",
      isPanelVisible && "rotate-180",
    ),

    dropdown: cx(
      "flex flex-col z-10 min-w-[187px] transition-all duration-500 ease-in-out",
      isMyBH
        ? "absolute bg-neutral-200 text-neutral-700 text-xxs leading-[18px] p-xs top-[24px] right-0 gap-xs"
        : "bg-neutral-700 lg:bg-neutral-200 text-text-inverse lg:text-neutral-700 text-sm lg:text-xxs leading-[22px] lg:leading-[18px] p-lg py-xs lg:p-xs gap-sm lg:gap-xs top-[24px] right-0 lg:absolute",
      isPanelVisible ? "opacity-100" : "opacity-0 hidden",
    ),

    dropdownMenuItems: cx(isMyBH ? "py-0" : "py-xxs lg:py-0"),
  };

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className={cx(rootClassName)}>
      <button
        className={styles.button}
        onClick={(e) => {
          e.stopPropagation();
          setIsPanelVisible(!isPanelVisible);
        }}
      >
        <div className={styles.container}>
          <img
            className={styles.profileIcon}
            src={iconProfile}
            alt="profile-icon"
            width={12}
            height={12}
          />
          <span className={styles.profileName}>
            Hi,{" "}
            {ENABLE_MYBH
              ? `${userInfo.firstName} ${userInfo.lastName}`
              : userInfo.loginId}
          </span>
          <img
            className={styles.desktopArrow}
            src={slideDownIcon}
            alt="slide-down-icon"
            width={12}
            height={12}
          />
          {!isMyBH && !isPanelVisible && (
            <img
              className="lg:hidden"
              src={slideDownIconMobile}
              alt="slide-down-icon-mobile"
              width={14}
              height={14}
            />
          )}
          {!isMyBH && isPanelVisible && (
            <img
              className="lg:hidden"
              src={slideUpIconMobile}
              alt="slide-up-icon-mobile"
              width={14}
              height={14}
            />
          )}
        </div>
      </button>

      {
        <span id="sph_user_name" className="hidden">
          {encodeURI(userInfo.loginId)}
        </span>
      }

      <ul id="user-info-panel" className={styles.dropdown}>
        <li>
          <a
            target="_blank"
            href={`https://subscribe.sph.com.sg/your-account/?utm_campaign=manageaccount&utm_medium=sph-publication&utm_source=bt&utm_content=manageaccountlink-dropdown&pub-code=bt${encodeURIComponent(
              currentUrl,
            )}`}
            rel="noreferrer"
            className={styles.dropdownMenuItems}
          >
            Urus Akaun
          </a>
          {/* {userInfo.userStatus === "unverified" ? (
            <li>
              <button onClick={mySPHOpenResendVerificationMail}>
                <span className="text-neutral-300 lg:text-text-muted">
                  Sila sahkan e-mel anda
                </span>
              </button>
            </li>
          ) : null} */}
        </li>
        <li>
          <button
            className={styles.dropdownMenuItems}
            onClick={() => lightbox?.initiateUpdatePassword()}
          >
            Kemas kini kata laluan anda
          </button>
        </li>
        {ENABLE_MYBH && (
          <li>
            <a
              href={`/${MANAGE_TOPICS_LINK}`}
              className={styles.dropdownMenuItems}
            >
              Urus notifikasi dan topik
            </a>
          </li>
        )}
        <li>
          <button
            className={styles.dropdownMenuItems}
            onClick={() => lightbox?.initiateLogout()}
          >
            Log keluar
          </button>
        </li>
      </ul>
    </div>
  );
}
